// Decouples the API layer from the auth store: api code (and a global fetch
// interceptor) can publish a "session lapsed" signal without importing the
// store (no import cycle). The store registers the handler at startup.

export type LockReason = 'session-expired';

let lockHandler: ((reason: LockReason) => void) | null = null;

export function registerLockHandler(fn: (reason: LockReason) => void): void {
  lockHandler = fn;
}

export function notifyLocked(reason: LockReason = 'session-expired'): void {
  lockHandler?.(reason);
}

// Purge the service worker's cached authed GETs (on lock / logout) so a stale
// response can't be served behind the lock. Page-side delete + SW postMessage
// fallback (covers the case where the page-side Cache API is restricted).
export async function clearApiCache(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') await caches.delete('api-cache');
  } catch {
    /* ignore */
  }
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_API_CACHE' });
  } catch {
    /* ignore */
  }
}

// ─── Global fetch interceptor ────────────────────────────────────
// Web and API are same-origin (Caddy serves the SPA and proxies /api), so the
// browser already sends the session cookie on every /api call. We only need to
// notice when the server says the session lapsed. Wrapping window.fetch once
// catches EVERY call site — the typed request() wrapper, the ~20 raw streaming
// fetches, react-query, everything — without editing each one.
//
// /api/auth/* is exempt: a failed unlock/enroll (400/401) must NOT trigger the
// lock (those flows handle their own errors), or we'd loop. The auth endpoints
// that legitimately need to lock (e.g. heartbeat) handle it explicitly.
let installed = false;

export function installAuthFetch(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const original = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await original(input, init);
    try {
      if (response.status === 401) {
        const raw =
          typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.pathname
              : input instanceof Request
                ? input.url
                : String(input);
        const path = raw.startsWith('http') ? new URL(raw).pathname : raw;
        if (path.startsWith('/api/') && !path.startsWith('/api/auth/')) {
          notifyLocked('session-expired');
        }
      }
    } catch {
      /* never let interception throw into the caller */
    }
    return response;
  };
}
